/**
 * rapportUtils.js
 * Utilitaires partagés pour les rapports de reprise (Sinistres, Encaissements, Paiements).
 * Importer dans chaque page Reprise.
 */
import { XLSX } from './xlsxShim';
import axiosInstance from '../variables/api/axiosInstance';
import i18n from '../i18n';
import { libellesTraduits } from '../i18n/libelles';

/* Rapport interne au Bureau : il suit la langue de l'interface. Textes sous
   referentiels:rapportReprise, lus au moment de produire le rapport. */
const t = (cle, options) => i18n.t(`referentiels:rapportReprise.${cle}`, options);

/**
 * Télécharge le rapport (comportement habituel) ET l'envoie au backend qui
 * l'archive automatiquement sur disque dans
 * <upload-dir>/rapports/<type>/. En reprise locale (backend sur la machine de
 * l'utilisateur), le fichier atterrit directement sur son disque - zéro clic.
 * L'échec d'archivage n'empêche jamais le téléchargement.
 *
 * @param {object} wb     classeur shim
 * @param {string} nom    nom de fichier
 * @param {('encaissements'|'paiements'|'sinistres')} type  sous-dossier cible
 * @param {number} annee  année de la reprise
 */
async function telechargerEtArchiver(wb, nom, type, annee) {
    const buffer = await XLSX.toBuffer(wb);
    XLSX.download(buffer, nom);                 // téléchargement classique conservé
    try {
        const form = new FormData();
        form.append('type', type);
        if (annee != null) form.append('annee', String(annee));
        form.append('file', new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }), nom);
        await axiosInstance.post('/v1/reprise/rapports', form);
    } catch (e) {
        // Archivage best-effort : on n'interrompt pas l'utilisateur si ça échoue.
        console.warn('[rapport] archivage backend échoué :', e?.message || e);
    }
}

//  Raisons lisibles par catégorie 
export const RAISONS = libellesTraduits('referentiels:rapportReprise.raisons');

//  Actions correctives par catégorie 
export const ACTIONS = libellesTraduits('referentiels:rapportReprise.actions');

//  Catégorisation automatique d'une erreur 
export function categoriserErreur(d) {
    if (d.type === 'DOUBLON') return 'DOUBLON';
    if (d.type === 'SINISTRE_CREE') return 'SINISTRE_CREE';
    if (d.type === 'VICTIME_CREEE') return 'VICTIME_CREEE';
    if (d.type === 'SANS_ENCAISSEMENT') return 'SANS_ENCAISSEMENT';
    if (d.type === 'NUMERO_SINISTRE_CORRIGE') return 'NUMERO_SINISTRE_CORRIGE';
    const msg = (d.message || '').toLowerCase();
    if (msg.includes('pays')) return 'ERROR_PAYS';
    if (msg.includes('organisme') || msg.includes('introuvable') || msg.includes('compagnie')) return 'ERROR_ORG';
    if (msg.includes('date') || msg.includes('format')) return 'ERROR_DATE';
    if (msg.includes('sinistre')) return 'ERROR_SINISTRE';
    if (msg.includes('montant') || msg.includes('nul')) return 'ERROR_MONTANT';
    return 'ERROR_OTHER';
}

//  Tag couleur pour l'affichage 
export const TAG_COLORS = {
    ERROR: 'red',
    DOUBLON: 'orange',
    SINISTRE_CREE: 'blue',
    VICTIME_CREEE: 'cyan',
    SANS_ENCAISSEMENT: 'purple',
};

//  Couleur texte action corrective 
export function couleurAction(cat) {
    if (cat === 'SINISTRE_CREE' || cat === 'VICTIME_CREEE') return '#92400e';
    if (cat === 'DOUBLON') return '#6b7280';
    if (cat === 'SANS_ENCAISSEMENT') return '#6b21a8';
    if (cat === 'NUMERO_SINISTRE_CORRIGE') return '#1d4ed8';
    return '#991b1b';
}

//  Nom de fichier avec horodatage 
function nomFichier(base) {
    const now = new Date();
    const date = now.toLocaleDateString('fr-FR').replace(/\//g, '-');
    const h = now.toLocaleTimeString('fr-FR').replace(/:/g, 'h');
    return `${base}_${date}_${h}_by_CBConnect.xlsx`;
}

//  Feuille résumé commune 
function feuilleResume(rapport, titre, lignesExtra = []) {
    const pct = (n) => rapport.totalTraite
        ? `${((n / rapport.totalTraite) * 100).toFixed(1)}%` : '-';

    const rows = [
        [titre, '', '', ''],
        [t('resume.genereLe'), rapport.date, '', ''],
        [],
        [t('resume.synthese'), t('resume.nombre'), t('resume.pctTotal'), t('resume.statut')],
        [t('resume.totalTraitees'), rapport.totalTraite, '100%', ''],
        [t('resume.importes'), rapport.importes, pct(rapport.importes), 'OK'],
        [t('resume.doublons'), rapport.doublons, pct(rapport.doublons), t('resume.ignore')],
        [t('resume.creationsAuto'),
            (rapport.details || []).filter(d => d.type === 'SINISTRE_CREE' || d.type === 'VICTIME_CREEE').length,
            '', t('resume.aCompleter')],
        [t('resume.numerosCorriges'),
            (rapport.details || []).filter(d => d.type === 'NUMERO_SINISTRE_CORRIGE').length,
            '', 'AUTO'],
        [t('resume.erreursRejetees'), rapport.erreurs, pct(rapport.erreurs), t('resume.rejete')],
        ...lignesExtra,
        [],
        [t('resume.legende'), '', '', ''],
        [t('colonnes.type'), t('resume.signification'), t('resume.actionRecommandee'), ''],
        ...Object.entries(RAISONS).map(([k, v]) => [k, v, ACTIONS[k], '']),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
    return ws;
}

//  Feuille anomalies commune 
function feuilleAnomalies(details, titre, colsSupp = []) {
    const rows = [
        [titre], [],
        [t('colonnes.ligne'), t('colonnes.type'), t('colonnes.numeroReference'), t('colonnes.raisonLisible'), t('colonnes.actionCorrective'),
            ...colsSupp.map(c => c.label), t('colonnes.messageTechnique')],
    ];
    details.forEach(d => {
        const cat = categoriserErreur(d);
        rows.push([
            d.ligne ? `L${d.ligne}` : '-',
            d.type,
            d.numero || '',
            RAISONS[cat] || d.message,
            ACTIONS[cat] || t('messages.interventionManuelle'),
            ...colsSupp.map(c => d[c.key] || ''),
            d.message || '',
        ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const baseW = [{ wch: 8 }, { wch: 16 }, { wch: 22 }, { wch: 42 }, { wch: 52 }];
    ws['!cols'] = [...baseW, ...colsSupp.map(() => ({ wch: 20 })), { wch: 55 }];
    return ws;
}

//  Feuille créations auto commune 
function feuilleCreationsAuto(details, titre) {
    const autos = details.filter(d => d.type === 'SINISTRE_CREE' || d.type === 'VICTIME_CREEE');
    if (!autos.length) return null;
    const rows = [
        [titre],
        [t('messages.creationsMinimales')],
        [],
        [t('colonnes.ligne'), t('colonnes.typeCreation'), t('colonnes.numeroReferenceMinuscule'), t('colonnes.ceQuiAEteCree'), t('colonnes.actionAMener')],
    ];
    autos.forEach(d => {
        rows.push([
            d.ligne ? `L${d.ligne}` : '-',
            d.type,
            d.numero || '',
            d.message || '',
            t('messages.ouvrirDossier'),
        ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 8 }, { wch: 16 }, { wch: 22 }, { wch: 55 }, { wch: 55 }];
    return ws;
}

//  Feuille des numéros de sinistre corrigés (nomenclature)
function feuilleNumerosCorriges(details, titre) {
    const corr = details.filter(d => d.type === 'NUMERO_SINISTRE_CORRIGE');
    if (!corr.length) return null;
    const rows = [
        [titre],
        [t('messages.numerosRamenes')],
        [t('messages.ligneRattachee')],
        [],
        [t('colonnes.ligne'), t('colonnes.numeroReference'), t('colonnes.correctionAppliquee')],
    ];
    corr.forEach(d => {
        rows.push([
            d.ligne ? `L${d.ligne}` : '-',
            d.numero || '',
            d.message || '',
        ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 8 }, { wch: 24 }, { wch: 70 }];
    return ws;
}

//  Feuille avertissements parsing communs
function feuilleAvertissements(lignes, titre, getNumero, getAssure) {
    const averts = lignes.filter(r => (r._warnings || r.warnings || []).length > 0);
    if (!averts.length) return null;
    const rows = [
        [titre],
        [t('messages.lignesPartielles')],
        [],
        [t('colonnes.ligne'), t('colonnes.numeroReference'), t('colonnes.partieConcernee'), t('colonnes.avertissementsDetectes'), t('colonnes.impactPossible')],
    ];
    averts.forEach(r => {
        const warns = r._warnings || r.warnings || [];
        const impact = warns.map(w => {
            if (w.includes('Date')) return t('impacts.date');
            if (w.includes('Assuré') || w.includes('Bénéf')) return t('impacts.partie');
            if (w.includes('Pays')) return t('impacts.pays');
            if (w.includes('Dommage')) return t('impacts.dommage');
            if (w.includes('Montant') || w.includes('nul')) return t('impacts.montant');
            return t('impacts.autre');
        }).join(' | ');
        rows.push([
            r._ligne ? `L${r._ligne}` : '-',
            getNumero(r),
            getAssure(r),
            warns.join(' | '),
            impact,
        ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 28 }, { wch: 50 }, { wch: 50 }];
    return ws;
}

// ══════════════════════════════════════════════════════════════════
//  Export rapport ENCAISSEMENTS
// ══════════════════════════════════════════════════════════════════
export async function exporterRapportEncaissements(rapport, annee) {
    if (!rapport) return;
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb,
        feuilleResume(rapport, t('titres.encaissements', { annee })),
        t('feuilles.resume'));

    // Feuille 2 : Importés OK
    const okRows = [
        [t('titres.encaissementsImportes', { annee })], [],
        [t('colonnes.ligne'), t('colonnes.numeroSinistre'), t('colonnes.numeroCheque'), t('colonnes.payeur'), t('colonnes.dateReception'),
            t('colonnes.montantCheque'), t('colonnes.principal'), t('colonnes.avertissements')],
    ];
    (rapport.lignesOk || []).forEach(r => {
        okRows.push([
            r._ligne ? `L${r._ligne}` : '-',
            r.numeroSinistreManuel || '',
            r.numeroCheque || '',
            r.organismeEmetteurCode || '',
            r.dateReception || '',
            r._montantCheque || 0,
            r.montantTheorique || 0,
            (r._warnings || []).join(' | ') || '',
        ]);
    });
    const wsOk = XLSX.utils.aoa_to_sheet(okRows);
    wsOk['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsOk, t('feuilles.importesOk'));

    // Feuille 3 : Anomalies
    const anomalies = (rapport.details || []).filter(d =>
        d.type === 'DOUBLON' || d.type === 'ERROR');
    XLSX.utils.book_append_sheet(wb,
        feuilleAnomalies(anomalies,
            t('titres.anomaliesEncaissements', { annee }),
            [{ key: 'assure', label: t('colonnes.assure') }, { key: 'dateAccident', label: t('colonnes.dateSinistre') }]),
        t('feuilles.anomalies3'));

    // Feuille 4 : Créations auto
    const wsAuto = feuilleCreationsAuto(rapport.details || [],
        t('titres.creationsEncaissements', { annee }));
    if (wsAuto) XLSX.utils.book_append_sheet(wb, wsAuto, t('feuilles.creationsAuto4'));

    // Feuille 5 : Avertissements
    const wsAvert = feuilleAvertissements(rapport.lignesOk || [],
        t('titres.avertissementsParsing', { annee }),
        r => r.numeroCheque || '',
        r => r.assureNomComplet || r.organismeEmetteurCode || '');
    if (wsAvert) XLSX.utils.book_append_sheet(wb, wsAvert, t('feuilles.avertissements5'));

    // Feuille 6 : Numéros de sinistre corrigés (nomenclature)
    const wsCorr = feuilleNumerosCorriges(rapport.details || [],
        t('titres.numerosCorrigesEncaissements', { annee }));
    if (wsCorr) XLSX.utils.book_append_sheet(wb, wsCorr, t('feuilles.numerosCorriges6'));

    await telechargerEtArchiver(wb, nomFichier(`Rapport_Reprise_Encaissements_${annee}`), 'encaissements', annee);
}

// ══════════════════════════════════════════════════════════════════
//  Export rapport PAIEMENTS
// ══════════════════════════════════════════════════════════════════
export async function exporterRapportPaiements(rapport, annee) {
    if (!rapport) return;
    const wb = XLSX.utils.book_new();

    const nbSansEnc = (rapport.details || [])
        .filter(d => d.type === 'SANS_ENCAISSEMENT').length;

    XLSX.utils.book_append_sheet(wb,
        feuilleResume(rapport, t('titres.paiements', { annee }), [
            nbSansEnc > 0
                ? [t('resume.sansEncaissementLie'), nbSansEnc, '', t('resume.rapprochementRequis')]
                : null,
        ].filter(Boolean)),
        t('feuilles.resume'));

    // Feuille 2 : Importés OK
    const okRows = [
        [t('titres.paiementsImportes', { annee })], [],
        [t('colonnes.ligne'), t('colonnes.numeroSinistre'), t('colonnes.numeroChequePaiement'), t('colonnes.numeroChequeEncaissement'),
            t('colonnes.payeur'), t('colonnes.beneficiaire'), t('colonnes.datePaiement'), t('colonnes.montant'),
            t('colonnes.encLie'), t('colonnes.avertissements')],
    ];
    (rapport.lignesOk || []).forEach(r => {
        okRows.push([
            r._ligne ? `L${r._ligne}` : '-',
            r.numeroSinistreManuel || '',
            r.numeroChequeEmis || '',
            r.numeroChequeEncaissement || '',
            r.organismePayeurCode || '',
            r.beneficiaire || '',
            r.datePaiement || '',
            r._montant || 0,
            r.numeroChequeEncaissement ? t('messages.oui') : t('messages.non'),
            (r._warnings || []).join(' | ') || '',
        ]);
    });
    const wsOk = XLSX.utils.aoa_to_sheet(okRows);
    wsOk['!cols'] = [
        { wch: 8 }, { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 14 },
        { wch: 25 }, { wch: 13 }, { wch: 16 }, { wch: 8 }, { wch: 40 },
    ];
    XLSX.utils.book_append_sheet(wb, wsOk, t('feuilles.importesOk'));

    // Feuille 3 : Sans encaissement (spécifique paiements)
    const sansEnc = [
        ...(rapport.lignesOk || []).filter(r => !r.numeroChequeEncaissement),
        ...(rapport.details || []).filter(d => d.type === 'SANS_ENCAISSEMENT'),
    ];
    if (sansEnc.length > 0) {
        const seRows = [
            [t('titres.paiementsSansEncaissement', { annee })],
            [t('messages.sansChequeEncaissement')],
            [t('messages.actionRequise')],
            [],
            [t('colonnes.ligne'), t('colonnes.numeroSinistre'), t('colonnes.numeroChequePaiement'), t('colonnes.payeur'),
                t('colonnes.beneficiaire'), t('colonnes.datePaiement'), t('colonnes.montant'), t('colonnes.message')],
        ];
        sansEnc.forEach(r => {
            seRows.push([
                r._ligne ? `L${r._ligne}` : (r.ligne ? `L${r.ligne}` : '-'),
                r.numeroSinistreManuel || r.numero || '',
                r.numeroChequeEmis || '',
                r.organismePayeurCode || '',
                r.beneficiaire || '',
                r.datePaiement || '',
                r._montant || 0,
                r.message || t('messages.chequeEncaissementAbsent'),
            ]);
        });
        const wsSe = XLSX.utils.aoa_to_sheet(seRows);
        wsSe['!cols'] = [
            { wch: 8 }, { wch: 22 }, { wch: 20 }, { wch: 14 },
            { wch: 25 }, { wch: 13 }, { wch: 16 }, { wch: 55 },
        ];
        XLSX.utils.book_append_sheet(wb, wsSe, t('feuilles.sansEncaissement3'));
    }

    // Feuille 4 : Anomalies
    const anomalies = (rapport.details || []).filter(d =>
        d.type === 'DOUBLON' || d.type === 'ERROR');
    XLSX.utils.book_append_sheet(wb,
        feuilleAnomalies(anomalies,
            t('titres.anomaliesPaiements', { annee }),
            [{ key: 'beneficiaire', label: t('colonnes.beneficiaire') }, { key: 'datePaiement', label: t('colonnes.datePaiement') }]),
        t('feuilles.anomalies4'));

    // Feuille 5 : Créations auto
    const wsAuto = feuilleCreationsAuto(rapport.details || [],
        t('titres.creationsPaiements', { annee }));
    if (wsAuto) XLSX.utils.book_append_sheet(wb, wsAuto, t('feuilles.creationsAuto5'));

    // Feuille 6 : Avertissements
    const wsAvert = feuilleAvertissements(rapport.lignesOk || [],
        t('titres.avertissementsParsing', { annee }),
        r => r.numeroChequeEmis || '',
        r => r.beneficiaire || '');
    if (wsAvert) XLSX.utils.book_append_sheet(wb, wsAvert, t('feuilles.avertissements6'));

    // Feuille 7 : Numéros de sinistre corrigés (nomenclature)
    const wsCorr = feuilleNumerosCorriges(rapport.details || [],
        t('titres.numerosCorrigesPaiements', { annee }));
    if (wsCorr) XLSX.utils.book_append_sheet(wb, wsCorr, t('feuilles.numerosCorriges7'));

    await telechargerEtArchiver(wb, nomFichier(`Rapport_Reprise_Paiements_${annee}`), 'paiements', annee);
}